import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { revokeMyApiKey, type ApiKeySummary } from "../lib/api";
import { Button } from "./ui/button";

type Props = {
  keys: ApiKeySummary[];
  showOwner?: boolean;
};

function status(k: ApiKeySummary): string {
  if (k.revoked_at) return "Widerrufen";
  if (k.expires_at && new Date(k.expires_at) < new Date()) return "Abgelaufen";
  return "Aktiv";
}

function fmt(ts: string | null): string {
  if (!ts) return "—";
  return ts.replace("T", " ").slice(0, 19);
}

export function KeyTable({ keys, showOwner = false }: Props) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: revokeMyApiKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me", "api-keys"] });
      toast.success("Key widerrufen");
    },
    onError: () => toast.error("Widerruf fehlgeschlagen"),
  });

  if (keys.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Noch keine Keys angelegt.</p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left">
          <tr>
            <th className="px-3 py-2">Label</th>
            <th className="px-3 py-2">Prefix</th>
            {showOwner && <th className="px-3 py-2">Owner</th>}
            <th className="px-3 py-2">Erstellt</th>
            <th className="px-3 py-2">Zuletzt benutzt</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const active = !k.revoked_at;
            return (
              <tr key={k.id} className="border-t">
                <td className="px-3 py-2 font-medium">{k.label}</td>
                <td className="px-3 py-2 font-mono">{k.prefix}…</td>
                {showOwner && (
                  <td className="px-3 py-2">{k.owner_email ?? "—"}</td>
                )}
                <td className="px-3 py-2">{fmt(k.created_at)}</td>
                <td className="px-3 py-2">{fmt(k.last_used_at)}</td>
                <td className="px-3 py-2">{status(k)}</td>
                <td className="px-3 py-2 text-right">
                  {active && !showOwner && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Key "${k.label}" wirklich widerrufen? Das kann nicht rückgängig gemacht werden.`,
                          )
                        ) {
                          revoke.mutate(k.id);
                        }
                      }}
                      disabled={revoke.isPending}
                    >
                      Widerrufen
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
